import { cn } from "../../utils/cn";
import { LucideIcon } from "lucide-react";

export interface SopTypeOption {
  id: string;
  label: string;
  description?: string;
  disabled?: boolean;
  icon?: LucideIcon;
}

export interface SopTypeSelectorProps {
  options: SopTypeOption[];
  selectedType: string | null;
  onTypeChange: (typeId: string) => void;
  className?: string;
  /** Override the section title */
  title?: string;
}

export default function SopTypeSelector({
  options,
  selectedType,
  onTypeChange,
  className,
  title = "Set context for type of SOP to generate",
}: SopTypeSelectorProps) {
  return (
    <div className={cn("space-y-4", className)}>
      <div className="border border-[var(--color-fg)]/20 rounded-lg p-4">
        <h3 className="font-semibold text-xl mb-3">{title}</h3>
        <div className="grid grid-cols-1 gap-2" role="radiogroup">
          {options.map((option) => {
            const isSelected = selectedType === option.id;
            const Icon = option.icon;
            return (
              <div
                key={option.id}
                className={cn(
                  "relative rounded-lg transition-all duration-300 ease-in-out p-[2px]",
                  isSelected
                    ? "bg-gradient-to-r from-[#3C6AC6] via-[#D216CC] to-[#FB6B96]"
                    : "bg-transparent",
                )}
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => !option.disabled && onTypeChange(option.id)}
                  disabled={option.disabled}
                  className={cn(
                    "flex items-center gap-3 w-full p-3 rounded-lg text-left transition-all duration-200",
                    isSelected
                      ? "bg-[var(--color-bg)]"
                      : "bg-[var(--color-bg)] border border-[var(--color-fg)]/20",
                    option.disabled
                      ? "opacity-50 cursor-not-allowed"
                      : !isSelected
                        ? "hover:border-[var(--color-accent)]/40 hover:bg-[var(--color-fg)]/5 cursor-pointer"
                        : "",
                  )}
                >
                  <div
                    className={cn(
                      "w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-all duration-200",
                      isSelected
                        ? "bg-gradient-to-br from-blue-500/30 to-purple-500/30 text-blue-300"
                        : option.disabled
                          ? "bg-slate-800/30 text-slate-600"
                          : "bg-slate-800/50 text-slate-500",
                    )}
                  >
                    {Icon ? (
                      <Icon className="w-4 h-4" />
                    ) : (
                      <div className="w-2 h-2 rounded-full bg-current" />
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    <div
                      className={cn(
                        "font-medium text-base leading-tight transition-colors duration-200",
                        isSelected
                          ? "text-white"
                          : option.disabled
                            ? "text-[var(--color-fg)]/40"
                            : "text-slate-300",
                      )}
                    >
                      {option.label}
                    </div>
                    {option.description && (
                      <div
                        className={cn(
                          "text-sm mt-0.5 leading-relaxed",
                          isSelected
                            ? "text-slate-400"
                            : option.disabled
                              ? "text-[var(--color-fg)]/25"
                              : "text-[var(--color-fg)]/55",
                        )}
                      >
                        {option.description}
                      </div>
                    )}
                  </div>

                  {option.disabled ? (
                    <span className="text-[10px] text-slate-600 shrink-0 border border-slate-700/40 rounded px-1.5 py-0.5 leading-tight">
                      Soon
                    </span>
                  ) : null}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
